/**
 * Input sampler (05-input §1, §2, §3 gamepad, §7 edge cases).
 * THROW/DASH/JUMP are edge-latched (a press+release inside one tick still fires
 * once); SHIELD is a level. Move is screen-space; aim is a world bearing
 * supplied by the aim model (floorAim for mouse, stick for gamepad).
 */
import type { PlayerInput, Team } from '../../sim/types.js'

export type Action = 'throw' | 'jump' | 'shield' | 'dash' | 'queue' | 'spectate' | 'menu' | 'camera' | 'feedback'

export interface Bindings { throw: string[]; jump: string[]; shield: string[]; dash: string[]; queue: string[]; spectate: string[]; menu: string[]; camera: string[]; feedback: string[] }
/** 05 §2 (Sprint 10): Space jumps, throw keeps LMB alone; pad A jumps, dash moved to LB / B. */
export const DEFAULT_BINDINGS: Bindings = {
  throw: ['Mouse0'], jump: ['Space'], shield: ['Mouse2', 'KeyE'], dash: ['ShiftLeft', 'ShiftRight', 'KeyF'],
  queue: ['Tab'], spectate: ['KeyQ'], menu: ['Escape'], camera: ['F8'],
  /** 13 §2 playtest marker prompt. */
  feedback: ['Slash'],
}

export interface GamepadOptions { aimAssist: boolean; curve: 'linear' | 'squared'; leftDeadzone: number; rightDeadzone: number }

export interface SamplerEvents { queue?: () => void; spectate?: () => void; menu?: () => void; camera?: () => void; feedback?: () => void }

/** Minimal event source so the sampler is testable without a DOM. */
export interface KeySource {
  onKey(fn: (code: string, down: boolean) => void): void
  onMouse(fn: (button: number, down: boolean) => void): void
  onMouseMove(fn: (x: number, y: number, inside: boolean) => void): void
  /** Relative mouse motion while the pointer is locked (free third-person camera), in CSS px. */
  onLook?(fn: (dx: number) => void): void
  onBlur(fn: () => void): void
}

/** The slice of the Gamepad API the sampler reads (standard mapping) — injectable so tests can drive a pad without `navigator`. */
export interface PadLike { axes: readonly number[]; buttons: readonly { pressed: boolean }[] }
/** Default pad source: the first standard-mapping gamepad, or null. */
export function navigatorPad(): PadLike | null {
  const pads = typeof navigator !== 'undefined' && navigator.getGamepads ? navigator.getGamepads() : []
  for (const g of pads) if (g && g.mapping === 'standard') return g
  return null
}

/** Camera turn per CSS pixel of locked-pointer mouse travel. */
export const LOOK_RAD_PER_PX = 0.0022
/** Camera turn rate at full right-stick deflection (rad/s). */
export const PAD_LOOK_RAD_PER_S = 2.6

export class InputSampler {
  private down = new Set<string>()
  private throwLatch = false
  private dashLatch = false
  private jumpLatch = false
  /** Virtual-pixel cursor; null = outside the canvas (aim frozen). */
  cursor: { x: number; y: number } | null = null
  lastCursor = { x: 640, y: 500 }
  /** Last world aim angle (radians); updated by the caller via setAim. */
  aim = Math.PI / 2
  team: Team = 0
  bindings: Bindings = DEFAULT_BINDINGS
  // 05 §3: assist on by default for gamepad (09's options screen will expose it).
  gamepad: GamepadOptions = { aimAssist: true, curve: 'squared', leftDeadzone: 0.18, rightDeadzone: 0.25 }
  /** 05 §6 / 09 `a11y.holdToToggleShield`: a tap toggles the held level; the wire still carries a level (A05-10). */
  holdToToggleShield = false
  private shieldToggled = false
  private shieldHeldPrev = false
  events: SamplerEvents = {}
  private lastAimDevice: 'mouse' | 'pad' = 'mouse'
  private padPrev = { throw: false, dash: false, jump: false, queue: false }
  private padThrowLatch = false
  private padDashLatch = false
  private padJumpLatch = false
  private padLastAim: { x: number; z: number } | null = null
  private padActive = false
  private lookPx = 0
  /** Right-stick x from the last pad poll (−1..1), for camera look in free-camera rooms. */
  padLookX = 0
  /** Free third-person camera: the right stick turns the camera and the aim comes from the fixed reticle
   *  (the caller sets `aim` directly); stick aim and mouse-aim device switching are off. */
  lookMode = false

  constructor(src?: KeySource, private readonly padSource: () => PadLike | null = navigatorPad) {
    src?.onKey((code, down) => this.key(code, down))
    src?.onMouse((b, down) => this.key(`Mouse${b}`, down))
    src?.onMouseMove((x, y, inside) => { if (inside) { this.cursor = { x, y }; this.lastCursor = { x, y }; this.lastAimDevice = 'mouse' } else this.cursor = null })
    src?.onLook?.((dx) => { this.lookPx += dx })
    src?.onBlur(() => this.blur())
  }

  /** Accumulated locked-pointer travel (CSS px) since the last take; resets. */
  takeLook(): number { const v = this.lookPx; this.lookPx = 0; return v }

  key(code: string, down: boolean): void {
    if (down && this.down.has(code)) return
    if (down) this.down.add(code); else this.down.delete(code)
    if (down) {
      if (this.holdToToggleShield && this.bindings.shield.includes(code)) this.shieldToggled = !this.shieldToggled
      if (this.bindings.throw.includes(code)) this.throwLatch = true
      if (this.bindings.jump.includes(code)) this.jumpLatch = true
      if (this.bindings.dash.includes(code)) this.dashLatch = true
      if (this.bindings.queue.includes(code)) this.events.queue?.()
      if (this.bindings.spectate.includes(code)) this.events.spectate?.()
      if (this.bindings.menu.includes(code)) this.events.menu?.()
      if (this.bindings.camera.includes(code)) this.events.camera?.()
      if (this.bindings.feedback.includes(code)) this.events.feedback?.()
    }
  }

  /** Window blur (05 §7): release levels, clear latches. */
  blur(): void {
    this.down.clear()
    this.throwLatch = this.dashLatch = this.jumpLatch = false
    this.padThrowLatch = this.padDashLatch = this.padJumpLatch = false
    this.shieldToggled = false
  }

  /** Called by the options layer when the toggle option flips; a lowered shield is the safe reset. */
  setHoldToToggle(on: boolean): void { this.holdToToggleShield = on; this.shieldToggled = false }

  private any(codes: string[]): boolean { return codes.some((c) => this.down.has(c)) }

  get usingGamepad(): boolean { return this.padActive && this.lastAimDevice === 'pad' }

  /** Poll the pad between samples (05 §7: a press + release inside one tick still latches its edge). */
  pollPadNow(): void { this.pollPad() }

  /** Poll the Gamepad API (standard mapping). Returns stick-space aim if the
   *  right stick is live. Buttons (05 §2): RT throw, LT shield, A jump, LB or B dash, Start queue. */
  private pollPad(): { mx: number; my: number; aim: { x: number; z: number } | null; throw: boolean; shield: boolean; dash: boolean; jump: boolean } {
    const none = { mx: 0, my: 0, aim: null, throw: false, shield: false, dash: false, jump: false }
    const gp = this.padSource()
    if (!gp) { this.padActive = false; return none }
    const curve = (v: number) => (this.gamepad.curve === 'squared' ? Math.sign(v) * v * v : v)
    let lx = gp.axes[0] ?? 0, ly = gp.axes[1] ?? 0
    const ll = Math.hypot(lx, ly)
    const dzL = this.gamepad.leftDeadzone
    if (ll < dzL) { lx = 0; ly = 0 } else { const s = curve(Math.min(1, (ll - dzL) / (1 - dzL))) / ll; lx *= s; ly *= s }
    // D-pad digital.
    if (gp.buttons[12]?.pressed) ly = -1
    if (gp.buttons[13]?.pressed) ly = 1
    if (gp.buttons[14]?.pressed) lx = -1
    if (gp.buttons[15]?.pressed) lx = 1
    const rx = gp.axes[2] ?? 0, ry = gp.axes[3] ?? 0
    this.padLookX = Math.abs(rx) >= this.gamepad.rightDeadzone ? rx : 0
    let aim: { x: number; z: number } | null = null
    if (!this.lookMode && Math.hypot(rx, ry) >= this.gamepad.rightDeadzone) { aim = { x: rx, z: -ry }; this.padLastAim = aim; this.lastAimDevice = 'pad' }
    const thr = gp.buttons[7]?.pressed ?? false
    const shd = gp.buttons[6]?.pressed ?? false
    const jmp = gp.buttons[0]?.pressed ?? false
    const dsh = (gp.buttons[4]?.pressed || gp.buttons[1]?.pressed) ?? false
    const que = gp.buttons[9]?.pressed ?? false
    if (thr && !this.padPrev.throw) this.padThrowLatch = true
    if (dsh && !this.padPrev.dash) this.padDashLatch = true
    if (jmp && !this.padPrev.jump) this.padJumpLatch = true
    if (que && !this.padPrev.queue) this.events.queue?.()
    this.padPrev = { throw: thr, dash: dsh, jump: jmp, queue: que }
    const active = ll > 0 || aim !== null || thr || shd || dsh || jmp
    if (active) this.padActive = true
    return { mx: lx, my: -ly, aim, throw: thr, shield: shd, dash: dsh, jump: jmp }
  }

  /** Set the mouse-derived world aim (computed by the caller with floorAim). */
  setMouseAim(angle: number): void { if (this.lastAimDevice === 'mouse') this.aim = angle }

  /**
   * Sample one tick. `padAimToWorld` converts a stick vector (screen-space)
   * to a world bearing; `assistTarget` optionally snaps within 6 deg.
   */
  sample(t: number, padAimToWorld?: (v: { x: number; z: number }) => number, assist?: (angle: number) => number | null): Omit<PlayerInput, 'seq' | 'tick'> {
    const pad = this.pollPad()
    let mx = (this.any(['KeyD', 'ArrowRight']) ? 1 : 0) - (this.any(['KeyA', 'ArrowLeft']) ? 1 : 0) + pad.mx
    let my = (this.any(['KeyW', 'ArrowUp']) ? 1 : 0) - (this.any(['KeyS', 'ArrowDown']) ? 1 : 0) + pad.my
    const l = Math.hypot(mx, my)
    if (l > 1) { mx /= l; my /= l }
    if (this.lastAimDevice === 'pad' && padAimToWorld) {
      const v = pad.aim ?? this.padLastAim
      if (v) {
        let a = padAimToWorld(v)
        if (this.gamepad.aimAssist && assist) { const s = assist(a); if (s !== null) a = s }
        this.aim = a
      }
    }
    const shieldRaw = this.any(this.bindings.shield) || pad.shield
    let shield = shieldRaw
    if (this.holdToToggleShield) {
      // Pad: edge on the shield button toggles too.
      if (pad.shield && !this.shieldHeldPrev) this.shieldToggled = !this.shieldToggled
      shield = this.shieldToggled
    }
    this.shieldHeldPrev = pad.shield
    const throwPressed = this.throwLatch || this.padThrowLatch
    const dashPressed = this.dashLatch || this.padDashLatch
    const jumpPressed = this.jumpLatch || this.padJumpLatch
    this.throwLatch = this.dashLatch = this.jumpLatch = false
    this.padThrowLatch = this.padDashLatch = this.padJumpLatch = false
    return {
      mx, my, aim: this.aim,
      throw: throwPressed,
      shield,
      dash: dashPressed,
      jump: jumpPressed,
      t,
    }
  }
}

/** DOM adapter. */
export function domKeySource(canvas: HTMLCanvasElement, toVirtual: (cx: number, cy: number) => { x: number; y: number }): KeySource {
  return {
    onKey(fn) {
      window.addEventListener('keydown', (e) => {
        if (e.repeat) return
        if (['Space', 'Tab', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'F8'].includes(e.code)) e.preventDefault()
        fn(e.code, true)
      })
      window.addEventListener('keyup', (e) => fn(e.code, false))
    },
    onMouse(fn) {
      canvas.addEventListener('mousedown', (e) => fn(e.button, true))
      window.addEventListener('mouseup', (e) => fn(e.button, false))
      canvas.addEventListener('contextmenu', (e) => e.preventDefault())
    },
    onMouseMove(fn) {
      canvas.addEventListener('mousemove', (e) => { if (document.pointerLockElement === canvas) return; const p = toVirtual(e.clientX, e.clientY); fn(p.x, p.y, true) })
      canvas.addEventListener('mouseleave', () => fn(0, 0, false))
    },
    onLook(fn) {
      // Chromium fires a spurious mousemove with a huge movementX right after the lock engages; drop the
      // first 50 ms after a lock change and any single delta over 300 px.
      let lockedAt = -Infinity
      document.addEventListener('pointerlockchange', () => { lockedAt = performance.now() })
      canvas.addEventListener('mousemove', (e) => {
        if (document.pointerLockElement !== canvas) return
        if (performance.now() - lockedAt < 50 || Math.abs(e.movementX) > 300) return
        fn(e.movementX)
      })
    },
    onBlur(fn) { window.addEventListener('blur', fn) },
  }
}
